import { useState, FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import { isChronicEnabled } from '../config/appMode';

import {
  User,
  Mail,
  Lock,
  Trash2,
  Save,
  AlertTriangle,
  Bell,
  Shield,
  Eye,
  EyeOff,
  Target,
  Activity,
  Heart
} from 'lucide-react';
import axios from 'axios';

// Traduceri în română pentru obiectivele de sănătate (GoalType enum)
const HEALTH_GOAL_LABELS: Record<string, string> = {
  WEIGHT_LOSS: 'Vreau să slăbesc',
  MUSCLE_GAIN: 'Cresc masa musculară',
  HEALTHY_LIFESTYLE: 'Stil de viață sănătos',
  REDUCE_BELLY_FAT: 'Reduc grăsimea abdominală',
  INCREASE_ENERGY: 'Mai multă energie zilnic',
  CLEAN_EATING: 'Mănânc mai curat și natural',
  REDUCE_SUGAR_CARBS: 'Reduc zahărul și carbohidrații',
  BETTER_SLEEP: 'Dorm mai bine',
  REDUCE_BLOATING: 'Scap de balonare',
  START_EXERCISE: 'Încep să fac sport',
  IMPROVE_FOCUS: 'Îmbunătățesc concentrarea',
  MAINTAIN_WEIGHT: 'Mențin greutatea',
  HEALTHY_WEIGHT_GAIN: 'Cresc în greutate sănătos',
  EASY_STANDING: 'Mă ridic ușor de pe scaun',
  REDUCE_INFLAMMATION: 'Reduc inflamația',
  RECOVER_MUSCLE_STRENGTH: 'Recuperez forța musculară',
  CLIMB_STAIRS: 'Urc scările fără durere',
  REDUCE_JOINT_PAIN: 'Reduc durerile articulare',
  BREATHE_BETTER: 'Respir mai bine',
  STABILIZE_GLUCOSE: 'Stabilizez glicemia',
  IMPROVE_BALANCE: 'Îmbunătățesc echilibrul',
  REDUCE_CHRONIC_FATIGUE: 'Reduc oboseala cronică',
  IMPROVE_DIGESTION: 'Îmbunătățesc digestia',
  REGAIN_MOBILITY: 'Recapăt mobilitatea',
  PREVENT_DISEASE_WORSENING: 'Previn agravarea bolii',
  HEALTHY_WEIGHT_LOSS_CHRONIC: 'Slăbesc sănătos',
  CONTROL_CHRONIC_PAIN: 'Controlez durerile cronice',
  IMPROVE_LIFE_WITH_DISEASE: 'Îmbunătățesc viața cu boala',
};

const getHealthGoalLabel = (goal: string): string => {
  return HEALTH_GOAL_LABELS[goal] || goal.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l: string) => l.toUpperCase());
};

export default function SettingsPage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();

  // Account Info
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [email, setEmail] = useState(user?.email || '');

  // Password Change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Delete Account
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // UI States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Notifications
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [notifLoading, setNotifLoading] = useState(false);

  // Load push notification preference from backend on mount
  useEffect(() => {
    const loadPushPreference = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/api/notifications/preferences`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (typeof response.data.pushNotificationsEnabled === 'boolean') {
          setPushNotifications(response.data.pushNotificationsEnabled);
        }
      } catch {
        // Silently fail - use default true
      }
    };
    loadPushPreference();
  }, []);

  const handlePushNotificationsChange = async (enabled: boolean) => {
    setPushNotifications(enabled);
    setNotifLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(
        `${import.meta.env.VITE_API_URL}/api/notifications/preferences`,
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {
      // Revert on error
      setPushNotifications(!enabled);
    } finally {
      setNotifLoading(false);
    }
  };

  const handleUpdateAccount = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.put(
        `${import.meta.env.VITE_API_URL}/api/auth/profile`,
        { firstName, lastName, email },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update localStorage with the new user data returned from the server
      const updatedUser = response.data.user;
      if (updatedUser) {
        const storedUser = localStorage.getItem('user');
        const currentUser = storedUser ? JSON.parse(storedUser) : {};
        const mergedUser = { ...currentUser, ...updatedUser };
        localStorage.setItem('user', JSON.stringify(mergedUser));
        // Refresh the AuthContext so the name updates everywhere in the app
        refreshUser();
      }

      setSuccess('Date actualizate cu succes! ✅');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la actualizare');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword.length < 6) {
      setPasswordError('Parola trebuie să aibă minim 6 caractere');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Parolele nu coincid');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/auth/change-password`,
        { currentPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPasswordSuccess('Parolă schimbată cu succes! ✅');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch (err: any) {
      setPasswordError(err.response?.data?.error || 'Eroare la schimbare parolă');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      alert('Te rog scrie DELETE pentru confirmare');
      return;
    }

    if (!confirm('Ești absolut sigur? Această acțiune este IREVERSIBILĂ!')) {
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/api/auth/account`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Cont șters cu succes. Îți vom duce dorul! 👋');
      logout();
      navigate('/');
    } catch (err: any) {
      alert(err.response?.data?.error || 'Eroare la ștergere cont');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader title="Setări Cont" />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Account Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center mb-6">
              <User className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-semibold">Informații Cont</h2>
            </div>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-600 text-sm">{success}</p>
              </div>
            )}

            <form onSubmit={handleUpdateAccount} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prenume *
                  </label>
                  <input
                    type="text"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nume *
                  </label>
                  <input
                    type="text"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Mail className="w-4 h-4 mr-2" />
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {loading ? 'Se salvează...' : 'Salvează Modificările'}
                </button>
              </div>
            </form>
          </div>

          {/* Change Password */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center mb-6">
              <Lock className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-semibold">Schimbă Parola</h2>
            </div>

            {passwordError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{passwordError}</p>
              </div>
            )}

            {passwordSuccess && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-600 text-sm">{passwordSuccess}</p>
              </div>
            )}

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Parola Curentă *
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    required
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Parolă Nouă * (minim 6 caractere)
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmă Parola Nouă *
                </label>
                <input
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400 flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  {loading ? 'Se schimbă...' : 'Schimbă Parola'}
                </button>
              </div>
            </form>
          </div>

          {/* Lifestyle Mode — vizibil DOAR în varianta CHRONIC a aplicației.
              În FII FIT (FITNESS) toți utilizatorii sunt forțați pe HEALTHY,
              deci selectorul "Sunt sănătos / Am afecțiune" nu mai are sens. */}
          {isChronicEnabled() && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center mb-6">
                <Activity className="w-6 h-6 text-blue-600 mr-3" />
                <h2 className="text-xl font-semibold">Mod Sănătate</h2>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Schimbă modul în care este personalizată aplicația pentru tine
              </p>

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => navigate('/lifestyle-mode')}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all text-left"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Activity className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-gray-900">Sunt Sănătos</span>
                  </div>
                  <p className="text-xs text-gray-600">Wellness & Fitness</p>
                </button>

                <button
                  onClick={() => navigate('/lifestyle-mode')}
                  className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <Heart className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-gray-900">Am Afecțiune</span>
                  </div>
                  <p className="text-xs text-gray-600">Management Medical</p>
                </button>
              </div>
            </div>
          )}

          {/* Health Goals */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center mb-4">
              <Target className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-semibold">Obiective de Sănătate</h2>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Schimbă sau adaugă obiectivele tale de sănătate oricând dorești
            </p>

            {/* Current goals preview */}
            {user?.healthGoals && Array.isArray(user.healthGoals) && user.healthGoals.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                {user.healthGoals.slice(0, 4).map((goal: string, idx: number) => (
                  <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    🎯 {getHealthGoalLabel(goal)}
                  </span>
                ))}
                {user.healthGoals.length > 4 && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                    +{user.healthGoals.length - 4} mai multe
                  </span>
                )}
              </div>
            )}

            <button
              onClick={() => navigate('/health-goal')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium flex items-center gap-2"
            >
              <Target className="w-4 h-4" />
              Schimbă Obiectivele
            </button>
          </div>

          {/* Calorie Goals - Link to dedicated page */}
          <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-lg shadow-sm p-6 border-2 border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">Obiective Calorice</h2>
                  <p className="text-sm text-gray-600">
                    Setează sau modifică obiectivele tale calorice zilnice
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/calorie-goals-setup')}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-medium shadow-md hover:shadow-lg"
              >
                Configurează →
              </button>
            </div>
          </div>

          {/* Notifications */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center mb-6">
              <Bell className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-semibold">Notificări</h2>
            </div>

            <div className="space-y-4">
              <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
                <div>
                  <p className="font-medium text-gray-900">Notificări Email</p>
                  <p className="text-sm text-gray-500">Primește actualizări pe email</p>
                </div>
                <input
                  type="checkbox"
                  checked={emailNotifications}
                  onChange={(e) => setEmailNotifications(e.target.checked)}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
              </label>

              <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
                <div>
                  <p className="font-medium text-gray-900">Notificări Push</p>
                  <p className="text-sm text-gray-500">
                    Primește notificări în aplicație
                    {notifLoading && <span className="ml-2 text-xs text-blue-500">Se salvează...</span>}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={pushNotifications}
                  disabled={notifLoading}
                  onChange={(e) => handlePushNotificationsChange(e.target.checked)}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
              </label>

              {/* Reminders link */}
              <button
                onClick={() => navigate('/notifications')}
                className="w-full flex items-center justify-between p-4 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Bell className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900">Remindere & Alarme</p>
                    <p className="text-sm text-gray-500">Mese, suplimente, antrenamente, apa</p>
                  </div>
                </div>
                <svg className="w-5 h-5 text-purple-400 group-hover:text-purple-600 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Data Privacy Info */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <Shield className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-xl font-semibold text-blue-900">Confidențialitate & Date</h2>
            </div>

            <div className="space-y-3 text-sm text-gray-700">
              <p>
                Datele tale sunt protejate conform <strong>GDPR (Regulamentul UE 2016/679)</strong> și stocate securizat cu criptare.
              </p>
              <p>
                Ai dreptul la: acces la date, rectificare, ștergere, portabilitate și retragerea consimțământului în orice moment.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <a href="/privacy" className="text-blue-600 hover:text-blue-700 font-medium text-sm underline">
                  Politica de Confidențialitate
                </a>
                <span className="text-gray-400">|</span>
                <a href="/terms" className="text-blue-600 hover:text-blue-700 font-medium text-sm underline">
                  Termeni și Condiții
                </a>
                <span className="text-gray-400">|</span>
                <a href="mailto:office@fiifit.ro" className="text-blue-600 hover:text-blue-700 font-medium text-sm underline">
                  Contact DPO: office@fiifit.ro
                </a>
                <p className="text-xs text-gray-500 mt-1">
                  SC GONGA SHOP SRL · CUI: RO40071310 · Tel: 0787-569.002
                </p>
              </div>
            </div>
          </div>

          {/* Danger Zone - Delete Account */}
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
              <h2 className="text-xl font-semibold text-red-900">Zonă Periculoasă</h2>
            </div>

            <div className="text-sm text-gray-700 mb-4 space-y-2">
              <p>
                Odată ce ștergi contul, <strong>toate datele tale vor fi șterse permanent și ireversibil</strong>, inclusiv:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
                <li>Profilul și datele de sănătate</li>
                <li>Conversațiile cu AI și sesiunile terapeutice</li>
                <li>Planurile nutriționale și exercițiile generate</li>
                <li>Tracking-ul alimentar și jurnalul emoțional</li>
                <li>Abonamentul activ (fără rambursare)</li>
              </ul>
              <p className="text-red-700 font-medium">
                Această acțiune este conformă cu dreptul la ștergere GDPR (Art. 17) și nu poate fi anulată.
              </p>
            </div>

            <button
              onClick={() => setShowDeleteModal(true)}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Șterge Contul Definitiv
            </button>
          </div>
        </div>
      </main>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4 text-red-600">
              <AlertTriangle className="w-8 h-8 mr-3" />
              <h3 className="text-xl font-bold">Șterge Cont</h3>
            </div>

            <p className="text-gray-700 mb-4">
              Această acțiune este <strong>IREVERSIBILĂ</strong>. Toate datele tale vor fi șterse permanent.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Scrie <strong>DELETE</strong> pentru confirmare:
              </label>
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                className="w-full px-4 py-2 border-2 border-red-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="DELETE"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmation !== 'DELETE'}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Șterge Definitiv
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmation('');
                }}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                Anulează
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
